/* ==========================================================================
   nav-progress.js — 2px navigation progress line. Impact 6.3.0 / EnklSound.

   Loaded deferred from <head> so a click 200ms into page load still gets
   feedback. Styles live in assets/nav-transitions.css.

   DELIBERATELY ES5. No let/const, no arrow functions, no template literals,
   no class, no closest(). One modern token is a parse error, and a parse
   error means the whole file is dead in older Safari — silently.

   DELIBERATELY BUBBLE PHASE, and it respects e.defaultPrevented. Capture
   phase would paint a progress bar for clicks that an app handles instead of
   navigating. That is not hypothetical on this store: layout/theme.liquid
   binds a delegated jQuery handler to [name="checkout"] / [name="goto_pp"] /
   [name="goto_gc"] which alert()s and returns false when the terms checkbox
   is unticked — the highest-intent click on the site. jQuery's `return false`
   also stops propagation, so this listener never even runs for those.
   ========================================================================== */

(function () {
  'use strict';

  var SHOW_AFTER_MS = 0; /* prefetch-only navigations are rarely fast enough
                            for an immediate bar to read as a flicker */
  var SAFETY_MS = 8000; /* hard reset so the bar can never stick */
  var TRICKLE_MS = 220;
  var TRICKLE_CEILING = 0.9; /* never reach 1 until the page actually leaves */

  var bar = null;
  var showTimer = null;
  var safetyTimer = null;
  var trickleTimer = null;
  var progress = 0;
  var running = false;

  function build() {
    if (bar || !document.body) {
      return bar;
    }
    bar = document.createElement('div');
    bar.className = 'enkl-nav-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    return bar;
  }

  function paint() {
    if (bar) {
      bar.style.transform = 'scaleX(' + progress + ')';
    }
  }

  function clearTimers() {
    if (showTimer) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      safetyTimer = null;
    }
    if (trickleTimer) {
      clearInterval(trickleTimer);
      trickleTimer = null;
    }
  }

  function reset() {
    clearTimers();
    running = false;
    progress = 0;
    if (bar) {
      bar.removeAttribute('data-active');
      bar.style.transform = 'scaleX(0)';
    }
  }

  function start() {
    if (running) {
      return;
    }
    running = true;

    showTimer = setTimeout(function () {
      var el = build();
      if (!el) {
        return;
      }
      el.setAttribute('data-active', '');
      progress = 0.12;
      paint();

      trickleTimer = setInterval(function () {
        /* ease toward the ceiling, slowing as it approaches */
        progress = progress + (TRICKLE_CEILING - progress) * 0.25;
        if (progress > TRICKLE_CEILING) {
          progress = TRICKLE_CEILING;
        }
        paint();
      }, TRICKLE_MS);
    }, SHOW_AFTER_MS);

    safetyTimer = setTimeout(reset, SAFETY_MS);
  }

  function finish() {
    if (!running) {
      return;
    }
    clearTimers();
    progress = 1;
    paint();
  }

  /* --- anchor resolution, hand-rolled instead of closest() --- */
  function anchorFrom(node) {
    while (node && node !== document) {
      if (node.nodeName === 'A' && node.getAttribute('href') !== null) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  }

  function navigates(a, e) {
    if (e.defaultPrevented) {
      return false;
    }
    if (e.button !== 0) {
      return false;
    }
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return false;
    }
    if (a.hasAttribute('download')) {
      return false;
    }

    var target = a.getAttribute('target');
    if (target && target !== '_self') {
      return false;
    }

    /* different origin -> the browser may or may not leave; do not claim it */
    if (a.protocol && a.protocol !== ':' && a.href) {
      if (a.protocol !== 'http:' && a.protocol !== 'https:') {
        return false; /* mailto:, tel:, javascript: */
      }
      if (a.host && a.host !== window.location.host) {
        return false;
      }
    }

    /* same page, hash only -> no document navigation */
    if (
      a.pathname === window.location.pathname &&
      a.search === window.location.search &&
      a.hash
    ) {
      return false;
    }

    return true;
  }

  document.addEventListener(
    'click',
    function (e) {
      var a = anchorFrom(e.target);
      if (!a) {
        return;
      }
      if (!navigates(a, e)) {
        return;
      }
      start();
    },
    false /* BUBBLE — see header comment */
  );

  /* Form submits also navigate (search, localization, cart). */
  document.addEventListener(
    'submit',
    function (e) {
      if (e.defaultPrevented) {
        return;
      }
      var form = e.target;
      /* AJAX forms in this theme are custom elements that preventDefault, so
         anything reaching here unprevented is a real document navigation. */
      if (form && form.nodeName === 'FORM') {
        start();
      }
    },
    false
  );

  /* The page is actually leaving — fill the bar so the last frame captured in
     the outgoing view-transition snapshot is a complete one. */
  window.addEventListener('pagehide', finish, false);
  window.addEventListener('beforeunload', finish, false);

  /* Restored from the back/forward cache: the bar would still be filled. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      reset();
    }
  }, false);
})();
